import { deviceClient, deviceTrackerClient } from './api-grpc';
import {
  Device as DeviceV2,
  DeviceDriverType,
  DeviceSensorSewerLocation,
  DeviceSensorType,
  DeviceSensorUltrasonicModel,
  DeviceType,
  GetDeviceListByPageResponse as GetDeviceListByPageResponseV2,
} from '../gen/proto/api/gateway/v1/device_pb';
import { DeviceTracker, DeviceTrackerType } from '../gen/proto/api/gateway/v1/device_tracker_pb';

export const deviceApi = {
  async create(
    name: string,
    type: DeviceType,
    sensor?: {
      type: DeviceSensorType;
      sewerLocation: DeviceSensorSewerLocation;
      ultrasonicModel: DeviceSensorUltrasonicModel;
    },
    driver?: { model: DeviceDriverType },
    tenantUuid?: string,
  ): Promise<DeviceV2> {
    const sensorDevice =
      type === DeviceType.SENSOR && sensor
        ? {
            case: 'sensor' as const,
            value: {
              type: sensor.type,
              sewerLocation: sensor.sewerLocation,
              model: { case: 'ultrasonicModel' as const, value: sensor.ultrasonicModel },
            },
          }
        : undefined;

    const driverDevice =
      type === DeviceType.DRIVER && driver ? { case: 'driver' as const, value: { model: driver.model } } : undefined;

    const response = await deviceClient.createDevice({
      name,
      type,
      device: sensorDevice ?? driverDevice ?? { case: undefined },
      tenantUuid: tenantUuid ?? '',
    });

    if (!response.device) {
      throw new Error('Error al crear el dispositivo: no se devolvió ningún dispositivo.');
    }
    return response.device;
  },

  async deleteDevice(uuid: string): Promise<void> {
    await deviceClient.deleteDevice({ uuid });
    return;
  },

  async updateDevice(
    uuid: string,
    name: string,
    type: DeviceType,
    sensor?: {
      type: DeviceSensorType;
      sewerLocation: DeviceSensorSewerLocation;
      ultrasonicModel: DeviceSensorUltrasonicModel;
    },
    driver?: { model: DeviceDriverType },
    tenantUuid?: string,
  ): Promise<DeviceV2> {
    const sensorDevice =
      type === DeviceType.SENSOR && sensor
        ? {
            case: 'sensor' as const,
            value: {
              type: sensor.type,
              sewerLocation: sensor.sewerLocation,
              model: { case: 'ultrasonicModel' as const, value: sensor.ultrasonicModel },
            },
          }
        : undefined;

    const driverDevice =
      type === DeviceType.DRIVER && driver ? { case: 'driver' as const, value: { model: driver.model } } : undefined;

    const response = await deviceClient.updateDevice({
      uuid: uuid,
      name: name,
      type: type,
      device: sensorDevice ?? driverDevice ?? { case: undefined },
      tenantUuid: tenantUuid ?? '',
    });

    if (!response.device) {
      throw new Error('No device returned for uuid: ' + uuid);
    }
    return response.device;
  },

  async getListByPage(pageIndex: number, pageSize: number): Promise<GetDeviceListByPageResponseV2> {
    return await deviceClient.getDeviceListByPage({
      pagination: { pageIndex, pageSize },
    });
  },
  async getDeviceByUuid(uuid: string): Promise<DeviceV2> {
    const response = await deviceClient.getDeviceByUuid({ uuid });
    if (!response.device) {
      throw new Error('No device returned for uuid: ' + uuid);
    }
    return response.device;
  },

  async getCurrentLocation(deviceUuid: string): Promise<DeviceTracker | null> {
    const response = await deviceTrackerClient.getDeviceTracker({
      deviceUuid: deviceUuid,
    });
    return response.deviceTracker[0] ?? null;
  },

  async getLocationHistory(deviceUuid: string): Promise<DeviceTracker[]> {
    const response = await deviceTrackerClient.getDeviceTracker({
      deviceUuid: deviceUuid,
    });
    if (response.deviceTracker === null) {
      throw new Error('No device returned for device tracker');
    }
    return response.deviceTracker;
  },

  async newDeviceLocation(
    deviceUuid: string,
    installationUuid: string,
    movementType: DeviceTrackerType,
  ): Promise<void> {
    const response = await deviceTrackerClient.newDeviceTracker({
      deviceUuid: deviceUuid,
      movementType: movementType,
      installationUuid: installationUuid,
    });
    if (response.deviceTracker === null) {
      throw new Error('Could not add device tracker');
    }
  },

  async getDeviceByInstallation(installationUuid: string): Promise<DeviceV2[]> {
    const response = await deviceClient.getDeviceListByInstallationUuid({ installationUuid });
    if (!response.devices) {
      throw new Error('No device returned for device tracker');
    }
    return response.devices;
  },

  async getDevicesByName(name: string, pageIndex: number, pageSize: number): Promise<DeviceV2[] | null> {
    const response = await deviceClient.getDevicesByName({
      name,
      pagination: { pageIndex, pageSize },
    });
    return response.devices ?? null;
  },
};
